import { useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Download, Trash2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [passcode, setPasscode] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, {
        headers: { "X-Admin-Passcode": passcode },
      });
      setRows(data);
    } catch (err) {
      setRows(null);
      setError(err.response?.status === 401 ? "Incorrect passcode." : "Could not load messages.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Name", "Email", "Phone", "Account", "Topic", "Reference", "Message"];
    const lines = rows.map((m) =>
      [
        new Date(m.created_at).toLocaleString(),
        m.name,
        m.email,
        m.phone || "",
        m.account_number || "",
        m.topic,
        m.id,
        m.message,
      ].map(esc).join(",")
    );
    const csv = [header.map(esc).join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `concorde-messages-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, {
        headers: { "X-Admin-Passcode": passcode },
      });
      setRows(rows.filter((m) => m.id !== id));
    } catch (err) {
      setError("Could not delete the message. Try again.");
    }
  };

  return (
    <>
      <PageHeader
        testid="admin-header"
        overline="Admin"
        title="Contact form"
        accent="inbox."
        description="Messages submitted through the website contact form. This page is not linked publicly."
      />
      <section className="pb-24 lg:pb-32" data-testid="admin-body">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <form onSubmit={load} className="flex max-w-md gap-3" data-testid="admin-passcode-form">
              <div className="relative flex-1">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  data-testid="admin-passcode-input"
                  placeholder="Admin passcode"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="bg-white border-slate-900/15 text-slate-900 placeholder:text-slate-400 focus-visible:ring-ember h-12 pl-11"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-load-button"
                className="rounded-full bg-ember px-8 font-bold text-white transition-all duration-300 hover:bg-tangerine disabled:opacity-50"
              >
                {loading ? "Loading…" : "View"}
              </button>
            </form>
            {error && (
              <p className="mt-4 text-sm font-medium text-red-600" data-testid="admin-error">{error}</p>
            )}
          </Reveal>

          {rows && (
            <div className="mt-12" data-testid="admin-messages">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs tracking-[0.2em] text-slate-600 uppercase" data-testid="admin-count">
                  {rows.length} message{rows.length === 1 ? "" : "s"}
                </p>
                <div className="flex items-center gap-6">
                  <button
                    onClick={exportCsv}
                    data-testid="admin-export-button"
                    className="inline-flex items-center gap-2 text-sm font-bold text-ember hover:text-tangerine transition-colors duration-300"
                  >
                    <Download size={14} /> Download CSV
                  </button>
                  <button
                    onClick={() => load()}
                    data-testid="admin-refresh-button"
                    className="inline-flex items-center gap-2 text-sm font-bold text-ember hover:text-tangerine transition-colors duration-300"
                  >
                    <RefreshCw size={14} /> Refresh
                  </button>
                </div>
              </div>
              <div className="mt-6 space-y-4">
                {rows.map((m) => (
                  <article
                    key={m.id}
                    data-testid={`admin-message-${m.id.slice(0, 8)}`}
                    className="rounded-2xl border border-slate-900/10 bg-white p-6"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-display font-bold text-lg text-slate-900">
                        {m.name} <span className="font-body font-normal text-sm text-slate-600">· {m.email}</span>
                      </p>
                      <div className="flex items-center gap-4">
                        <time className="font-mono text-xs text-slate-600">
                          {new Date(m.created_at).toLocaleString()}
                        </time>
                        <button
                          onClick={() => remove(m.id)}
                          data-testid={`admin-delete-${m.id.slice(0, 8)}`}
                          aria-label="Delete message"
                          className="text-slate-400 hover:text-red-600 transition-colors duration-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs text-slate-600">
                      <span>TOPIC: {m.topic}</span>
                      {m.phone && <span>PHONE: {m.phone}</span>}
                      {m.account_number && <span>ACCT: {m.account_number}</span>}
                      <span>REF: {m.id.slice(0, 8).toUpperCase()}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </article>
                ))}
                {rows.length === 0 && (
                  <p className="text-slate-600" data-testid="admin-empty">No messages yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Admin;
